import { Button, Select } from './ui';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function TimeSeriesFilters({
  onFilterChange,
  onMetricsChange,
  onShowStdDevChange,
  industries = [],
  villages = [],
  regions = [],
  ministries = [],
  activeMetrics = [],
  allMetrics = [],
  showStdDev = true,
  filters = {
    industry: 'all',
    village: 'all',
    region: 'all',
    sector: 'all',
    ministry: 'all'
  }
}) {

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };

    // Reset ministry if sector changes to non-government
    if (key === 'sector' && value !== 'government') {
      newFilters.ministry = 'all';
    }

    onFilterChange(newFilters);
  };

  const resetFilters = () => {
    onFilterChange({
      industry: 'all',
      village: 'all',
      region: 'all',
      sector: 'all',
      ministry: 'all'
    });
  };

  const toggleMetric = (metricKey) => {
    const newMetrics = activeMetrics.includes(metricKey)
      ? activeMetrics.filter(m => m !== metricKey)
      : [...activeMetrics, metricKey];
    onMetricsChange(newMetrics);
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;

  return (
    <div className="space-y-3">
      {/* Row 1: Metric pills + Std Dev toggle */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-xs font-semibold text-text-primary-light dark:text-text-primary whitespace-nowrap">
          Metrics
        </span>
        <div className="flex flex-wrap gap-1.5">
          {allMetrics.map(metric => {
            const active = activeMetrics.includes(metric.key);
            return (
              <button
                key={metric.key}
                onClick={() => toggleMetric(metric.key)}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${
                  active
                    ? 'text-white border-transparent'
                    : 'border-border-light dark:border-border text-text-secondary-light dark:text-text-secondary hover:opacity-75'
                }`}
                style={active ? { backgroundColor: metric.color } : {}}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: metric.color }}
                />
                {metric.name}
              </button>
            );
          })}
        </div>

        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-text-secondary-light dark:text-text-secondary whitespace-nowrap hover:opacity-75 transition">
          <input
            type="checkbox"
            checked={showStdDev}
            onChange={(e) => onShowStdDevChange(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600"
          />
          Std Dev
        </label>
      </div>

      {/* Divider */}
      <div className="border-t border-border dark:border-border" />

      {/* Row 2: Filter selects */}
      <div className="flex flex-wrap gap-3 items-end">
        <span className="text-xs font-semibold text-text-primary-light dark:text-text-primary w-full">
          Filters
        </span>

        <div className="flex-1 min-w-[130px] max-w-[200px]">
          <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
            Industry
          </label>
          <Select
            value={filters.industry}
            onChange={(e) => handleFilterChange('industry', e.target.value)}
            className="text-sm"
          >
            <option value="all">All Industries</option>
            {industries.map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </Select>
        </div>

        <div className="flex-1 min-w-[120px] max-w-[180px]">
          <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
            Village
          </label>
          <Select
            value={filters.village}
            onChange={(e) => handleFilterChange('village', e.target.value)}
            className="text-sm"
          >
            <option value="all">All Villages</option>
            {villages.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </Select>
        </div>

        <div className="flex-1 min-w-[120px] max-w-[180px]">
          <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
            Region
          </label>
          <Select
            value={filters.region}
            onChange={(e) => handleFilterChange('region', e.target.value)}
            className="text-sm"
          >
            <option value="all">All Regions</option>
            {regions.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </div>

        <div className="flex-1 min-w-[120px] max-w-[180px]">
          <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
            Sector
          </label>
          <Select
            value={filters.sector}
            onChange={(e) => handleFilterChange('sector', e.target.value)}
            className="text-sm"
          >
            <option value="all">All Sectors</option>
            <option value="government">Government</option>
            <option value="non-government">Non-Government</option>
          </Select>
        </div>

        {filters.sector === 'government' && (
          <div className="flex-1 min-w-[130px] max-w-[200px]">
            <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
              Ministry
            </label>
            <Select
              value={filters.ministry}
              onChange={(e) => handleFilterChange('ministry', e.target.value)}
              className="text-sm"
            >
              <option value="all">All Ministries</option>
              {ministries.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Select>
          </div>
        )}

        {activeFilterCount > 0 && (
          <div className="self-end">
            <Button variant="secondary" size="sm" onClick={resetFilters} className="flex items-center gap-1">
              <XMarkIcon className="w-3 h-3" />
              Clear
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
